import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, useToastActions, ConfirmationModal } from '@airclou/ui';
import { Users } from 'lucide-react';
import {
  getGroupRemovalImpact,
  removeGroupFromWorkspace,
  type GroupRemovalImpact
} from '@/api/workspaces';

interface RemoveWorkspaceGroupModalProps {
  workspaceId: string;
  groupId: string;
  groupName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function RemoveWorkspaceGroupModal({
  workspaceId,
  groupId,
  groupName,
  isOpen,
  onClose,
  onSuccess
}: RemoveWorkspaceGroupModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: toastError } = useToastActions();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [impact, setImpact] = useState<GroupRemovalImpact | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchImpact();
    }
  }, [isOpen, workspaceId, groupId]);

  const fetchImpact = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const impactData = await getGroupRemovalImpact(workspaceId, groupId);
      setImpact(impactData);
    } catch (err: any) {
      setFetchError(err.message || t('workspaceTabs.failedToCalcImpact'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await removeGroupFromWorkspace(workspaceId, groupId);
      success(t('groups.groupRemovedFromWorkspace'), t('common.success'));
      onSuccess();
      onClose();
    } catch (err: any) {
      toastError(err.message || t('groups.failedToDelete'), t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="py-8 flex justify-center">
          <LoadingSpinner text={t('workspaceTabs.calculatingImpact')} />
        </div>
      );
    }

    if (fetchError) {
      return (
        <div className="py-4 text-center">
          <p className="text-sm text-red-600 dark:text-red-400">{fetchError}</p>
          <button
            onClick={fetchImpact}
            className="mt-2 text-sm text-primary hover:underline"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      );
    }

    if (!impact) return null;

    return (
      <div className="space-y-4">
        {/* Impact Summary */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <h3 className="text-sm font-medium text-foreground mb-3">
            {t('workspaceTabs.impactSummary')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {impact.will_lose_access}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('workspaceTabs.willLoseAccess')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {impact.will_retain_access}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('workspaceTabs.willRetainAccess')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {impact.will_lose_access > 0 && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>{t('common.warning')}:</strong> {t('workspaceTabs.warningUsersLoseAccess', { count: impact.will_lose_access, plural: impact.will_lose_access !== 1 ? 's' : '' })}
            </p>
          </div>
        )}

        {impact.will_retain_access > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('workspaceTabs.retainAccessThrough', { count: impact.will_retain_access, plural: impact.will_retain_access !== 1 ? 's' : '' })}
          </p>
        )}
      </div>
    );
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('workspaceTabs.removeGroupFromWorkspace')}
      description={t('workspaceTabs.removeGroupConfirm', { name: groupName })}
      variant="danger"
      confirmText={t('workspaceTabs.removeGroup')}
      cancelText={t('common.cancel')}
      onConfirm={handleRemove}
      isLoading={submitting}
      size="md"
    >
      {renderContent()}
    </ConfirmationModal>
  );
}
